import React, { useState } from 'react';
import { TextField, MenuItem } from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';

export interface InvoiceFilterValues {
    invoiceNumber: string;
    client: string;
    advance: string;
    pending: string;
    partialPaid: string;
    total: string;
    dateFrom: string;
    dateTo: string;
}

const EMPTY_FILTERS: InvoiceFilterValues = {
    invoiceNumber: '',
    client: '',
    advance: '',
    pending: '',
    partialPaid: '',
    total: '',
    dateFrom: '',
    dateTo: '',
};

interface InvoiceFiltersProps {
    invoiceNumbers: string[];
    clientNames: string[];
    onApply: (filters: InvoiceFilterValues) => void;
    onReset: () => void;
}

export const InvoiceFilters: React.FC<InvoiceFiltersProps> = ({
    invoiceNumbers,
    clientNames,
    onApply,
    onReset,
}) => {
    const [filters, setFilters] = useState<InvoiceFilterValues>(EMPTY_FILTERS);

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        onReset();
    };

    const fieldStyles = {
        '& .MuiInputBase-input': {
            // Hide up/down arrows (spinners) for type="number"
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                'WebkitAppearance': 'none',
                margin: 0,
            },
            '&[type=number]': {
                'MozAppearance': 'textfield',
            },
        },
    };

    const handleNumberKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
        }
    };

    const handleNumberWheel = (e: React.WheelEvent<HTMLDivElement>) => {
        (e.target as HTMLInputElement).blur();
    };

    return (
        <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-4 sm:p-5 mb-6">
            <div className="mb-4">
                <p className="text-xs font-bold uppercase tracking-widest text-[#6366f1]">Filters</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <TextField
                    select
                    label="Invoice Number"
                    value={filters.invoiceNumber}
                    onChange={(e) => setFilters((prev) => ({ ...prev, invoiceNumber: e.target.value }))}
                    fullWidth
                    size="small"
                >
                    <MenuItem value="">All Numbers</MenuItem>
                    {invoiceNumbers.map((num) => (
                        <MenuItem key={num} value={num}>{num}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    select
                    label="Client"
                    value={filters.client}
                    onChange={(e) => setFilters((prev) => ({ ...prev, client: e.target.value }))}
                    fullWidth
                    size="small"
                >
                    <MenuItem value="">All Clients</MenuItem>
                    {clientNames.map((name) => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="Advance Paid"
                    type="number"
                    value={filters.advance}
                    onChange={(e) => setFilters((prev) => ({ ...prev, advance: e.target.value }))}
                    onWheel={handleNumberWheel}
                    onKeyDown={handleNumberKeyDown}
                    fullWidth
                    size="small"
                    sx={fieldStyles}
                />

                <TextField
                    label="Total Pending"
                    type="number"
                    value={filters.pending}
                    onChange={(e) => setFilters((prev) => ({ ...prev, pending: e.target.value }))}
                    onWheel={handleNumberWheel}
                    onKeyDown={handleNumberKeyDown}
                    fullWidth
                    size="small"
                    sx={fieldStyles}
                />

                <TextField
                    label="Credited"
                    type="number"
                    value={filters.partialPaid}
                    onChange={(e) => setFilters((prev) => ({ ...prev, partialPaid: e.target.value }))}
                    onWheel={handleNumberWheel}
                    onKeyDown={handleNumberKeyDown}
                    fullWidth
                    size="small"
                    sx={fieldStyles}
                />

                <TextField
                    label="Total Amount"
                    type="number"
                    value={filters.total}
                    onChange={(e) => setFilters((prev) => ({ ...prev, total: e.target.value }))}
                    onWheel={handleNumberWheel}
                    onKeyDown={handleNumberKeyDown}
                    fullWidth
                    size="small"
                    sx={fieldStyles}
                />

                <TextField
                    label="Date From"
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    label="Date To"
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                />
            </div>

            <div className="flex gap-2 mt-4">
                <AppButton
                    variant="primary"
                    onClick={() => onApply(filters)}
                    sx={{ px: 3, py: 1, borderRadius: '6px' }}
                >
                    Search
                </AppButton>
                <AppButton
                    variant="secondary"
                    onClick={handleReset}
                    sx={{ px: 3, py: 1, borderRadius: '6px' }}
                >
                    Reset
                </AppButton>
            </div>
        </div>
    );
};

